import { useLocation, Link } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Briefcase,
  FileText,
  Heart,
  TrendingUp,
  Star,
  Menu,
  X,
  Brain,
  BarChart3,
  Video,
  DollarSign,
  Building2,
  User,
  Home
} from 'lucide-react'
import { HeaderAction } from '@/components/types/header'
import NotificationBell from './NotificationBell'
import UserMenu from './UserMenu'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

// Using HeaderAction interface imported above

export default function CandidateHeader() {
  const location = useLocation()
  const pathname = location.pathname
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const savedJobsEnabled = useFeatureFlag('savedJob')
  const companiesEnabled = useFeatureFlag('companies')
  const salaryFlagEnabled = useFeatureFlag('salary')
  const insightFlagEnabled = useFeatureFlag('insight')
  const interviewPrepEnabled = useFeatureFlag('interviewPrep')
  const careerAdviceEnabled = useFeatureFlag('careerAdvice')
  const takeAssessmentEnabled = useFeatureFlag('takeAssessment')
  const viewInsightsEnabled = useFeatureFlag('viewInsight')
  const salaryInsightsEnabled = salaryFlagEnabled || insightFlagEnabled
  const quickActions: HeaderAction[] = []
  
  const primaryNavigation = [
    { name: 'Find Jobs', href: '/jobs', icon: Search },
    { name: 'My Applications', href: '/candidate/applications', icon: FileText },
    { name: 'Resumes', href: '/candidate/resume', icon: FileText },
    { name: 'Career Profile', href: '/candidate/career-profile', icon: User },
  ]

  const rawSecondaryNavigation = [
    { name: 'Saved Jobs', href: '/candidate/saved-jobs', icon: Heart, enabled: savedJobsEnabled },
    { name: 'Companies', href: '/companies', icon: Building2, enabled: companiesEnabled },
    { name: 'Salary Insights', href: '/salary-insights', icon: DollarSign, enabled: salaryInsightsEnabled },
    { name: 'Interview Prep', href: '/interview-prep', icon: Video, enabled: interviewPrepEnabled },
    { name: 'Career Advice', href: '/career-advice', icon: TrendingUp, enabled: careerAdviceEnabled },
  ]

  const secondaryNavigation = rawSecondaryNavigation.filter((item) => item.enabled)
  const allNavigation = [...primaryNavigation, ...secondaryNavigation]

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center py-3 lg:py-4">
          {/* Logo and Company */}
          <div className="flex items-center space-x-2 lg:space-x-4 min-w-0">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="hidden sm:block min-w-0">
              <h1 className="text-lg lg:text-xl font-semibold text-gray-900 truncate">AI Recruiter</h1>
              <p className="text-xs lg:text-sm text-gray-500 hidden md:block">Find Your Dream Job</p>
            </div>
          </div>

          {/* Main Navigation */}
          <nav className="hidden lg:flex space-x-6">
            {primaryNavigation.map((item) => {
              const Icon = item.icon
              const isActive = pathname.startsWith(item.href)
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors relative ${
                    isActive
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:block">{item.name}</span>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-blue-50 rounded-md -z-10"
                      initial={false}
                      transition={{ type: "spring", duration: 0.3 }}
                    />
                  )}
                </Link>
              )
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Right side actions */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            <NotificationBell />

            {/* User Menu */}
            <UserMenu userType="candidate" />
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-gray-200 bg-white"
          >
            <div className="px-4 py-4 space-y-1">
              {allNavigation.map((item) => {
                const Icon = item.icon
                const isActive = pathname.startsWith(item.href)
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                )
              })}
              
              {/* Mobile Quick Actions */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Quick Actions</p>
                <div className="space-y-1">
                  {quickActions.map((action) => {
                    const Icon = action.icon
                    return (
                      <button
                        key={action.name}
                        onClick={() => {
                          action.onClick?.()
                          setShowMobileMenu(false)
                        }}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                          action.highlight 
                            ? 'text-blue-700 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 font-medium' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${action.highlight ? 'text-blue-600' : ''}`} />
                        <span className="flex-1 text-left">{action.name}</span>
                        {action.highlight && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">New</span>}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Tab Navigation for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex justify-around py-2">
          {primaryNavigation.map((item) => {
            const Icon = item.icon
            const isActive = pathname.startsWith(item.href)
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center space-y-1 px-2 py-1 rounded-lg transition-colors min-w-0 ${
                  isActive
                    ? 'text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-xs font-medium truncate">{item.name}</span>
              </Link>
            )
          })}
        </div>
      </div>
    </header>
  )
}
